import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import type { OrgOut, Prices, TierPrice } from "../api/types.gen.ts";
import { locale, negotiate, t } from "../i18n/index.ts";
import { formatPrice, loadPrices, payUrl, SALES_EMAIL, sold, tierName, usageText } from "../lib/billing.ts";
import { useOrg } from "../lib/data.ts";
import { formatInstant } from "../lib/format.ts";
import { spaceUrl } from "../lib/paths.ts";
import { type Period, type PlanCard, PRICING_COPY } from "../lib/pricing.ts";
import { useResource } from "../lib/resource.ts";
import { atLeast } from "../lib/roles.ts";
import { location, setQuery } from "../lib/router.ts";
import type { PageProps } from "../lib/state.ts";
import { toast } from "../lib/toast.ts";
import { Button, Link } from "../ui/Button.tsx";
import { Async, Badge, Notice, Section } from "../ui/misc.tsx";
import { PlanCards, PlanPrice } from "../ui/plans.tsx";
import { Spinner } from "../ui/Spinner.tsx";
import { buttonClass, LINK } from "../ui/styles.ts";
import { SettingsNav } from "./settings/nav.tsx";
import { PlanBanner } from "./settings/planBanner.tsx";
import { DATE, planDates, STATES } from "./settings/planState.ts";

/** How often the org is asked whether a payment's plan arrived (by webhook), and for how long. */
const POLL_MS = 2000;
const WAIT_MS = 90_000;
/** Refunds of yearly plans: in full within this many days of a first yearly purchase (the terms' rule). */
const REFUND_DAYS = 7;
/** The checkout site sends the browser back with `?confirming=<tier>` once paid. */
const CONFIRMING = "confirming";
const MONTHS = 12;
const BUSINESS = "business_";

/** What the org has: its plan and where it stands, until when; how much of it it uses is on the Usage tab. */
function Current({ org, usage }: { org: OrgOut; usage: string }) {
  const plan = org.plan;
  const { state, trial_ends_at: trialEnds, limits } = org.standing;
  const [stateName, stateTone] = STATES[state];
  const over = typeof limits?.people === "number" && org.counts.people > limits.people;
  return (
    <Section title={t("billing.current")}>
      <div class="flex max-w-xl flex-col gap-2 text-sm">
        <p class="flex flex-wrap items-center gap-2">
          <span class="text-base font-semibold text-zinc-900">{plan ? tierName(plan.tier) : t("billing.none")}</span>
          {trialEnds ? <Badge tone={stateTone}>{t(stateName)}</Badge> : null}
          {plan?.period ? <Badge>{t(plan.period === "yearly" ? "billing.yearly" : "billing.monthly")}</Badge> : null}
          {plan && !plan.renews && !plan.invoiced ? <Badge tone="amber">{t("billing.cancelled")}</Badge> : null}
        </p>
        {plan ? <p class="text-zinc-600">{planDates(plan)}</p> : null}
        {state === "trial" && trialEnds ? (
          <p class="text-zinc-600">{t("plan.trialEnds", { at: formatInstant(trialEnds, DATE) })}</p>
        ) : null}
        {over ? (
          <p class="font-medium text-red-700">
            {`${usageText(org.counts.people, limits?.people)}: ${t("billing.over")}`}
          </p>
        ) : null}
        {plan?.invoiced && sold() ? (
          <p class="text-zinc-600">{t("billing.invoicedHint", { email: SALES_EMAIL })}</p>
        ) : null}
        <Link href={usage} class={`${LINK} self-start`}>
          {t("billing.seeUsage")}
        </Link>
      </div>
    </Section>
  );
}

/** A link to the subscription's own page (through the checkout site), in a new tab. */
function ManageLink({
  href,
  class: cls = "",
  children,
}: {
  href: string;
  class?: string;
  children: ComponentChildren;
}) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" class={`${buttonClass()} ${cls}`}>
      {children}
    </a>
  );
}

/** Business' people to show first: the org's plan's, else the smallest that fits everyone, else the largest. */
function firstMembers(org: OrgOut, business: readonly TierPrice[]): string {
  const mine = business.find((x) => x.tier === org.plan?.tier);
  const fits = business.find((x) => x.members >= org.counts.people);
  return String((mine ?? fits ?? business.at(-1))?.members ?? "");
}

/**
 * The plans on sale, as the landing site shows them (`PlanCards`), priced by the server for the visitor's country.
 * Without a subscription: the checkout site, which sends the browser back here once paid. With one: changing it on its
 * subscription page (RevenueCat's, through the checkout site), as is cancelling or the payment method.
 */
function Cards({ org, pay, list }: { org: OrgOut; pay: string; list: Prices & { country: string | null } }) {
  const plan = org.plan;
  const lang = locale.value;
  const copy = PRICING_COPY[negotiate([lang]) as keyof typeof PRICING_COPY];
  const business = list.tiers.filter((x) => x.tier.startsWith(BUSINESS));
  const [period, setPeriod] = useState<Period>(plan?.period === "yearly" ? "yearly" : "monthly");
  const [members, setMembers] = useState(() => firstMembers(org, business));
  const back = location.value.pathname;
  const manage = payUrl(pay, "manage", { org: org.id, lang, back });
  // Off to the checkout site (the page stays until the browser leaves: a second click waits).
  const [leaving, setLeaving] = useState(false);
  const buy = (tier: string) => {
    setLeaving(true);
    globalThis.location.assign(
      payUrl(pay, "checkout", {
        org: org.id,
        tier,
        period,
        currency: list.currency,
        country: list.country,
        lang,
        back,
      }),
    );
  };
  const tierOf = (card: PlanCard) =>
    card === "team"
      ? list.tiers.find((x) => x.tier.startsWith("team"))
      : business.find((x) => String(x.members) === members);
  const price = (card: PlanCard) => {
    const tier = tierOf(card);
    const amount = period === "yearly" ? tier?.yearly : tier?.monthly;
    if (typeof amount !== "number") {
      return <p class="mt-5 text-sm text-zinc-600">{t("billing.atCheckout")}</p>;
    }
    const each = formatPrice(period === "yearly" ? amount / MONTHS : amount, list.currency, lang);
    const total = formatPrice(amount, list.currency, lang);
    const billed = period === "yearly" ? copy.billedYearly.replace("{total}", total) : copy.billedMonthly;
    return <PlanPrice copy={copy} amount={each} billed={billed} />;
  };
  const action = (card: PlanCard) => {
    const tier = tierOf(card);
    if (!tier) {
      return null;
    }
    if (plan?.tier === tier.tier && plan.period === period) {
      return (
        <Button disabled={true} class="w-full">
          {t("billing.yours")}
        </Button>
      );
    }
    if (plan) {
      return (
        <ManageLink href={manage} class="w-full">
          {t("billing.change")}
        </ManageLink>
      );
    }
    return (
      <Button
        variant={card === "team" ? "secondary" : "primary"}
        class="w-full"
        disabled={tier.members < org.counts.people}
        busy={leaving}
        onClick={() => buy(tier.tier)}
      >
        {t("billing.choose", { name: tierName(tier.tier) })}
      </Button>
    );
  };
  const note = (card: PlanCard) => {
    const tier = tierOf(card);
    return tier && !plan && tier.members < org.counts.people
      ? t("billing.tooSmall", { count: org.counts.people })
      : null;
  };
  const priced = list.tiers.some((x) => typeof x.monthly === "number" || typeof x.yearly === "number");
  return (
    <>
      <PlanCards
        copy={copy}
        period={period}
        onPeriod={setPeriod}
        members={members}
        onMembers={setMembers}
        options={business.map((x) => String(x.members))}
        price={price}
        action={action}
        note={note}
      />
      {priced ? <p class="mt-4 text-xs text-zinc-500">{t("billing.indicative", { currency: list.currency })}</p> : null}
    </>
  );
}

/** The plans on sale (or, with a subscription, a way to its page) and what buying means. */
function Store({ org, pay }: { org: OrgOut; pay: string }) {
  const prices = useResource(`prices:${locale.value}`, loadPrices);
  const plan = org.plan;
  const manage = payUrl(pay, "manage", { org: org.id, lang: locale.value, back: location.value.pathname });
  return (
    <Section title={t(plan ? "billing.changeTitle" : "billing.chooseTitle")}>
      <div class="flex max-w-6xl flex-col gap-4">
        {plan ? (
          <div class="flex flex-col items-start gap-2 text-sm text-zinc-600">
            <p>{t("billing.changeHint")}</p>
            <ManageLink href={manage}>{t("billing.manage")}</ManageLink>
          </div>
        ) : null}
        <div>
          <Async r={prices}>{(list) => <Cards org={org} pay={pay} list={list} />}</Async>
        </div>
        {plan ? null : <p class="max-w-2xl text-sm text-zinc-600">{t("billing.refunds", { days: REFUND_DAYS })}</p>}
      </div>
    </Section>
  );
}

/**
 * Back from the checkout (`?confirming=<tier>`), waiting for the payment's plan to arrive: the server hears of it from
 * the billing provider (a webhook), so the org is asked again until its plan is the one bought (the parameter is then
 * dropped), or a while has passed (reloading the page waits again).
 */
function useConfirming(org: OrgOut, reload: () => void) {
  const waiting = location.value.searchParams.get(CONFIRMING);
  const [late, setLate] = useState(false);
  // The latest reload (a new function every render), so polling doesn't restart with each.
  const again = useRef(reload);
  again.current = reload;
  const arrived = waiting !== null && org.plan?.tier === waiting;
  useEffect(() => {
    if (waiting === null || late) {
      return;
    }
    if (arrived) {
      setQuery({ [CONFIRMING]: null });
      toast(t("billing.active"));
      return;
    }
    const poll = setInterval(() => again.current(), POLL_MS);
    const give = setTimeout(() => {
      clearInterval(poll);
      setLate(true);
    }, WAIT_MS);
    return () => {
      clearInterval(poll);
      clearTimeout(give);
    };
  }, [waiting, arrived, late]);
  return { confirming: waiting !== null && !late, late: waiting !== null && late };
}

/** The org's plan, and (admins, where plans are sold) buying one or changing it. */
function PlanPage({ org, admin, reload, usage }: { org: OrgOut; admin: boolean; reload: () => void; usage: string }) {
  const { confirming, late } = useConfirming(org, reload);
  const pay = org.pay_url;
  return (
    <>
      <Notice when={confirming}>
        <span class="inline-flex items-center gap-2">
          <Spinner small={true} />
          {t("billing.confirming")}
        </span>
      </Notice>
      <Notice when={late}>{t("billing.late", { email: SALES_EMAIL })}</Notice>
      <Current org={org} usage={usage} />
      {/* In the iOS app, where it stands only (`sold`), and that it's not bought there. */}
      {sold() ? (
        admin ? (
          org.plan?.invoiced ? null : pay ? (
            <Store org={org} pay={pay} />
          ) : (
            <p class="text-sm text-zinc-600">{t("billing.notSold")}</p>
          )
        ) : (
          <p class="text-sm text-zinc-600">{t("billing.adminsOnly")}</p>
        )
      ) : (
        <p class="text-sm text-zinc-600">{t("billing.notInApp")}</p>
      )}
    </>
  );
}

/** The organization's plan and where it stands; admins buy one, or manage theirs. Its usage is on the Usage tab. */
export function Billing({ space }: PageProps) {
  const org = useOrg(space);
  return (
    <>
      <SettingsNav space={space} tab="/billing" />
      <PlanBanner space={space} here={true} />
      <Async r={org}>
        {(o) => (
          <PlanPage
            org={o}
            admin={atLeast(space.role, "admin")}
            reload={org.reload}
            usage={spaceUrl(space, "/settings/usage")}
          />
        )}
      </Async>
    </>
  );
}
